import { Calendar03Icon, ChevronDownIcon, UploadIcon } from '@/icons';
import { Button, DatePicker, Select, Space } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useGetAllShiftsQuery } from '../../../../../../app/features/employee/emp-attendance/emp-attendance.api';
import type { AttendanceParams } from '../../../../../../app/features/employee/emp-attendance/emp-attendance.type';
import { useGetAllDepartmentsQuery } from '../../../../../../app/features/employee/emp-dashboard/emp-dashboard.api';
// import { DownloadIcon } from '@/icons';
import ModuleGuard from '../../../../../../components/Guards/ModuleGuard';
import MODULES from '../../../../../../config/modules.config';
import { DB_DATE_FORMAT } from '../../../../../../constants/global.constants';
import useAuth from '../../../../../../hooks/useAuth';
import useDiscloser from '../../../../../../hooks/useDiscloser';
import useError from '../../../../../../hooks/useError';
import useModuleStatus from '../../../../../../hooks/useModuleStatus';
import { useQueryFilters } from '../../../../../../hooks/useQueryFilters';
import ChartIcon from '../../_icons/ChartIcon';
import BulkAttendanceUpload from './BulkAttendanceUpload';
import GenerateReportModal from './GenerateReportModal';

const { RangePicker } = DatePicker;

export default function SummaryFilter() {
  const [openUpload, setOpenUpload] = useState(false);
  const { isEnabled } = useModuleStatus();
  const { tenantId } = useAuth();
  const [config, handleSubOpen, handleSubClose] =
    useDiscloser<'GENERATE_REPORT'>();

  const { filters, setFilterValue, setFilterValues } =
    useQueryFilters<AttendanceParams>();
  const [isAllowClear, setIsAllowClear] = useState(false);

  const {
    isLoading: isDepartmentLoading,
    error: departmentError,
    data: departmentsData,
  } = useGetAllDepartmentsQuery({
    params: { page: 1, limit: 100, status: 'active' },
  });
  useError(departmentError, 'Failed to fetch department data');

  const {
    isLoading: isShiftLoading,
    error: shiftError,
    data: shiftData,
  } = useGetAllShiftsQuery(
    {
      params: {
        skip: 0,
        limit: 0,
        tenantId,
      },
    },
    { skip: !isEnabled(MODULES.EMPLOYEE_SHIFT_MANAGEMENT, 'route') },
  );
  useError(shiftError, 'Failed to fetch shift data');

  // Set default to current date if no dates are selected
  useEffect(() => {
    setTimeout(() => {
      if (!filters.startDate && !filters.endDate && !isAllowClear) {
        const today = dayjs().format(DB_DATE_FORMAT);
        setFilterValues({
          startDate: today,
          endDate: today,
        });
        setIsAllowClear(false);
      }
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate, isAllowClear]);

  return (
    <div className="flex flex-col items-end gap-3">
      <Space className="filter-section" wrap>
        <RangePicker
          size="large"
          placeholder={['Start Date', 'End Date']}
          className="w-80"
          suffixIcon={<Calendar03Icon size={16} />}
          value={
            filters.startDate && filters.endDate
              ? [
                  dayjs(filters.startDate, DB_DATE_FORMAT),
                  dayjs(filters.endDate, DB_DATE_FORMAT),
                ]
              : undefined
          }
          onChange={(dates) => {
            if (dates) {
              setFilterValues({
                startDate: dates[0]?.format(DB_DATE_FORMAT),
                endDate: dates[1]?.format(DB_DATE_FORMAT),
              });
            } else {
              setIsAllowClear(true);
              setFilterValues({
                startDate: undefined,
                endDate: undefined,
              });
            }
          }}
          showToday={false}
        />

        <ModuleGuard
          moduleId={MODULES.EMPLOYEE_SHIFT_MANAGEMENT}
          checkFor="route"
        >
          <Select
            size="large"
            placeholder="Shift"
            className="min-w-28"
            suffixIcon={<ChevronDownIcon size={24} />}
            popupMatchSelectWidth={200}
            allowClear
            value={filters.shiftId}
            onChange={(value) => setFilterValue('shiftId', value)}
            options={
              shiftData?.data?.data?.map((shift) => ({
                value: shift.id,
                label: shift.name,
              })) || []
            }
            disabled={isShiftLoading}
          />
        </ModuleGuard>
        <Select
          size="large"
          className="min-w-36"
          placeholder="Department"
          suffixIcon={<ChevronDownIcon size={24} />}
          popupMatchSelectWidth={200}
          value={filters.departmentId}
          onChange={(value) => setFilterValue('departmentId', value)}
          options={
            departmentsData?.data?.data?.map((department) => ({
              value: department.id,
              label: department.name,
            })) || []
          }
          disabled={isDepartmentLoading}
          allowClear
        />

        {/* Generate Report Modal */}
        {config.open && config.type === 'GENERATE_REPORT' && (
          <GenerateReportModal
            handleClose={handleSubClose}
            open={config.open}
          />
        )}

        <BulkAttendanceUpload
          open={openUpload}
          handleClose={() => setOpenUpload(false)}
        />
      </Space>
      <Space className="filter-section" wrap>
        <Button
          size="large"
          type="default"
          className="btn-primary"
          onClick={() => setOpenUpload(true)}
        >
          Upload CSV
          <UploadIcon size={20} className="!text-[#444444] !font-bold" />
        </Button>
        <Button
          size="large"
          className="btn-primary !text-neutral-700 !text-base 
            hover:!text-neutral-700 disabled:hover:opacity-80 font-bold"
          onClick={() => handleSubOpen({ type: 'GENERATE_REPORT' })}
        >
          Generate Report
          <ChartIcon className="text-base" />
        </Button>
      </Space>
    </div>
  );
}
